/**
 * CallHistoryPanel — Exotel call log display for a Lead / Patient
 * Shows all inbound/outbound calls linked to this lead, with Call Status, Follow-up Status, duration, recording, and disposition updates.
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  PhoneCall, 
  PhoneIncoming, 
  PhoneOff, 
  PhoneMissed, 
  Clock, 
  RefreshCw, 
  Loader2, 
  AlertTriangle, 
  XCircle, 
  MessageSquare,
  CheckCircle2,
  Calendar,
  CalendarCheck,
  Edit3,
  X,
  Check,
  UserCheck,
  Play,
  FileText
} from 'lucide-react';
import { getCallLogs, saveCallDisposition } from '../../../services/exotelApi';

// ── Helpers & Config ─────────────────────────────────────────────────────────

export const STATUS_CONFIG = {
  'call-successful':                 { icon: PhoneCall,     color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Call was successful' },
  'client-hungup-during-call':       { icon: PhoneOff,      color: 'text-rose-400',    bg: 'bg-rose-500/10 border-rose-500/20',       label: 'Client hung-up during call' },
  'client-hungup-before-connecting': { icon: PhoneOff,      color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',      label: 'Client hung-up before connecting' },
  'no-answer':                       { icon: PhoneMissed,   color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20',         label: 'No user answered (Missed)' },
  'no-user-answered':                { icon: PhoneMissed,   color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20',         label: 'No user answered (Missed)' },
  'client-unanswered':               { icon: PhoneMissed,   color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',      label: 'Client unanswered' },
  busy:                              { icon: PhoneOff,      color: 'text-orange-400',  bg: 'bg-orange-500/10 border-orange-500/20',    label: 'User was busy' },
  'in-progress':                     { icon: PhoneCall,     color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20',        label: 'Call in progress' },
  ringing:                           { icon: PhoneIncoming, color: 'text-indigo-400',  bg: 'bg-indigo-500/10 border-indigo-500/20',    label: 'Ringing' },
  waiting:                           { icon: Clock,          color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',     label: 'Waiting' },
  failed:                            { icon: PhoneOff,      color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20',          label: 'Failed' },

  // Fallbacks
  completed:                         { icon: PhoneCall,     color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Call was successful' },
  answered:                          { icon: PhoneCall,     color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Call was successful' },
  cancelled:                         { icon: PhoneOff,      color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',      label: 'Client hung-up before connecting' },
  'call-fatal':                      { icon: PhoneOff,      color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',      label: 'Client hung-up before connecting' },
  missed:                            { icon: PhoneMissed,   color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20',         label: 'No user answered' },
  rejected:                          { icon: PhoneOff,      color: 'text-rose-400',    bg: 'bg-rose-500/10 border-rose-500/20',       label: 'Rejected' },
  queued:                            { icon: Clock,          color: 'text-yellow-400',  bg: 'bg-yellow-500/10 border-yellow-500/20',    label: 'Queued' },
  initiated:                         { icon: PhoneCall,     color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20',        label: 'Initiated' },
};

export const DISPOSITION_CONFIG = {
  pending: {
    label: 'Pending Callback',
    badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    dot: 'bg-amber-400',
    icon: Clock
  },
  callback_completed: {
    label: 'Callback Completed',
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    dot: 'bg-emerald-400',
    icon: CheckCircle2
  },
  resolved: {
    label: 'Resolved',
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    dot: 'bg-emerald-400',
    icon: CheckCircle2
  },
  follow_up_needed: {
    label: 'Follow-up Needed',
    badge: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    dot: 'bg-blue-400',
    icon: Calendar
  },
  appointment_booked: {
    label: 'Appointment Booked',
    badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    dot: 'bg-purple-400',
    icon: CalendarCheck
  },
  not_interested: {
    label: 'Not Interested',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    dot: 'bg-slate-400',
    icon: XCircle
  },
  invalid_number: {
    label: 'Invalid / Wrong No.',
    badge: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    dot: 'bg-rose-400',
    icon: PhoneOff
  }
};

export const getEffectiveDisposition = (log) => {
  if (log.disposition && DISPOSITION_CONFIG[log.disposition]) {
    return log.disposition;
  }
  const isAnswered = ['call-successful', 'completed', 'answered'].includes(log.status);
  return isAnswered ? 'resolved' : 'pending';
};

export const getLightStatusBadge = (status) => {
  switch (status) {
    case 'call-successful':
    case 'completed':
    case 'answered':
      return {
        bg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        dot: 'bg-emerald-500',
        label: 'Call was successful'
      };
    case 'no-answer':
    case 'no-user-answered':
    case 'missed':
      return {
        bg: 'bg-red-50 text-red-700 border-red-200',
        dot: 'bg-red-500',
        label: 'No user answered (Missed)'
      };
    case 'client-hungup-during-call':
      return {
        bg: 'bg-rose-50 text-rose-700 border-rose-200',
        dot: 'bg-rose-500',
        label: 'Client hung-up during call'
      };
    case 'client-hungup-before-connecting':
    case 'cancelled':
    case 'call-fatal':
      return {
        bg: 'bg-amber-50 text-amber-700 border-amber-200',
        dot: 'bg-amber-500',
        label: 'Client hung-up before connecting'
      };
    case 'client-unanswered':
      return {
        bg: 'bg-amber-50 text-amber-700 border-amber-200',
        dot: 'bg-amber-500',
        label: 'Client unanswered'
      };
    case 'busy':
      return {
        bg: 'bg-orange-50 text-orange-700 border-orange-200',
        dot: 'bg-orange-500',
        label: 'User was busy'
      };
    case 'ringing':
      return {
        bg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        dot: 'bg-indigo-500 animate-ping',
        label: 'Ringing'
      };
    case 'in-progress':
      return {
        bg: 'bg-blue-50 text-blue-700 border-blue-200',
        dot: 'bg-blue-500 animate-pulse',
        label: 'Call in progress'
      };
    default:
      return {
        bg: 'bg-slate-50 text-slate-700 border-slate-200',
        dot: 'bg-slate-400',
        label: status || 'Unknown'
      };
  }
};

export const getLightDispositionBadge = (dispKey) => {
  switch (dispKey) {
    case 'callback_completed':
    case 'resolved':
      return {
        bg: 'bg-emerald-50 text-emerald-800 border-emerald-300',
        dot: 'bg-emerald-500',
        label: 'Callback Completed'
      };
    case 'follow_up_needed':
      return {
        bg: 'bg-blue-50 text-blue-800 border-blue-300',
        dot: 'bg-blue-500',
        label: 'Follow-up Needed'
      };
    case 'appointment_booked':
      return {
        bg: 'bg-purple-50 text-purple-800 border-purple-300',
        dot: 'bg-purple-500',
        label: 'Appointment Booked'
      };
    case 'not_interested':
      return {
        bg: 'bg-slate-100 text-slate-700 border-slate-300',
        dot: 'bg-slate-400',
        label: 'Not Interested'
      };
    case 'invalid_number':
      return {
        bg: 'bg-rose-50 text-rose-800 border-rose-300',
        dot: 'bg-rose-500',
        label: 'Invalid Number'
      };
    case 'pending':
    default:
      return {
        bg: 'bg-amber-50 text-amber-800 border-amber-300',
        dot: 'bg-amber-500',
        label: 'Pending Callback'
      };
  }
};


const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    const local = cleaned.slice(2);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }
  return phone;
};

// ── Component ─────────────────────────────────────────────────────────────────

const CallHistoryPanel = ({ leadId, refreshTrigger = 0, onLatestCallChange = null }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal State
  const [selectedLog, setSelectedLog] = useState(null);
  const [dispForm, setDispForm] = useState({
    disposition: 'callback_completed',
    dispositionNotes: '',
    callReason: ''
  });
  const [savingDisp, setSavingDisp] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getCallLogs(leadId, 50);
      setLogs(data);
      if (onLatestCallChange && data && data.length > 0) {
        onLatestCallChange(data[0]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load call history');
    } finally {
      setLoading(false);
    }
  }, [leadId, onLatestCallChange]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshTrigger]);

  const handleOpenModal = (log) => {
    setSelectedLog(log);
    const eff = getEffectiveDisposition(log);
    setDispForm({
      disposition: eff === 'pending' ? 'callback_completed' : eff,
      dispositionNotes: log.dispositionNotes || log.notes || '',
      callReason: log.callReason || ''
    });
    setSaveSuccessMsg(null);
  };

  const handleSaveDisposition = async (e) => {
    e.preventDefault();
    if (!selectedLog) return;
    setSavingDisp(true);
    try {
      const res = await saveCallDisposition(selectedLog._id, {
        disposition: dispForm.disposition,
        dispositionNotes: dispForm.dispositionNotes.trim(),
        notes: dispForm.dispositionNotes.trim(),
        callReason: dispForm.callReason.trim()
      });

      const updated = res?.data || {
        ...selectedLog,
        disposition: dispForm.disposition,
        dispositionNotes: dispForm.dispositionNotes.trim(),
        notes: dispForm.dispositionNotes.trim(),
        callReason: dispForm.callReason.trim(),
        dispositionUpdatedAt: new Date().toISOString()
      };

      setLogs(prev => prev.map(l => l._id === selectedLog._id ? { ...l, ...updated } : l));
      if (onLatestCallChange && logs[0]?._id === selectedLog._id) {
        onLatestCallChange({ ...selectedLog, ...updated });
      }

      setSaveSuccessMsg('Status updated!');
      setTimeout(() => {
        setSelectedLog(null);
        setSaveSuccessMsg(null);
      }, 1000);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to save status');
    } finally {
      setSavingDisp(false);
    }
  };

  return (
    <div className="bg-[#0f1117] border border-white/[0.07] rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
            <PhoneCall size={14} className="text-violet-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white leading-none">Call & Follow-up History</p>
            <p className="text-[10px] text-white/40 mt-0.5">
              {loading ? 'Loading…' : `${logs.length} record${logs.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/80 hover:bg-white/5 transition-all disabled:opacity-40"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        {loading && (
          <div className="flex items-center justify-center py-10 gap-2 text-white/40">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading call records…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center py-8 text-red-400/70 text-sm">
            <PhoneOff size={14} className="mr-2" />
            {error}
          </div>
        )}

        {!loading && !error && logs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-white/30">
            <PhoneCall size={24} className="opacity-50" />
            <p className="text-sm">No calls recorded yet</p>
            <p className="text-[11px]">Click "Call Patient" above to make the first call</p>
          </div>
        )}

        {!loading && !error && logs.length > 0 && (
          <div className="space-y-3">
            {logs.map((log) => {
              const cfg = STATUS_CONFIG[log.status] || {
                icon: PhoneCall,
                color: 'text-emerald-400',
                bg: 'bg-emerald-500/10 border-emerald-500/20',
                label: log.status ? (log.status.charAt(0).toUpperCase() + log.status.slice(1).replace(/-/g, ' ')) : 'Call was successful'
              };
              const StatusIcon = cfg.icon;
              const isInbound = log.direction === 'inbound';

              const effDisp = getEffectiveDisposition(log);
              const dispCfg = DISPOSITION_CONFIG[effDisp] || DISPOSITION_CONFIG.pending;
              const DispIcon = dispCfg.icon;

              const hasNotes = Boolean(log.dispositionNotes || log.notes);
              const hasUpdater = Boolean(log.dispositionUpdatedBy?.firstName);

              return (
                <div
                  key={log._id}
                  className="p-3.5 rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] transition-all"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    {/* Direction & Statuses */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                        isInbound ? 'bg-blue-500/20 text-blue-400' : 'bg-violet-500/20 text-violet-400'
                      }`}>
                        {isInbound ? <PhoneIncoming size={12} /> : <PhoneCall size={12} />}
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                        {isInbound ? 'Inbound' : 'Outbound'}
                      </span>

                      {/* 1. Call Status */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon size={9} />
                        {cfg.label}
                      </span>

                      {/* 2. Follow-up Status */}
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${dispCfg.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dispCfg.dot}`} />
                        <DispIcon size={10} />
                        {dispCfg.label}
                      </span>

                      {/* Duration */}
                      {log.duration > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-white/40">
                          <Clock size={9} />
                          {formatDuration(log.duration)}
                        </span>
                      )}
                    </div>

                    {/* Action button */}
                    <button
                      onClick={() => handleOpenModal(log)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 transition-all cursor-pointer"
                    >
                      <Edit3 size={10} />
                      Update Status
                    </button>
                  </div>

                  {/* Numbers & Agent info */}
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-white/60">
                    <span>
                      <span className="text-white/30 mr-1">From:</span>
                      {formatPhone(log.from)}
                    </span>
                    <span className="text-white/20">→</span>
                    <span>
                      <span className="text-white/30 mr-1">To:</span>
                      {log.assignedAgent ? (
                        <span className="text-white/90 font-medium">
                          {log.assignedAgent.firstName} {log.assignedAgent.lastName || ''}
                        </span>
                      ) : (
                        formatPhone(log.to)
                      )}
                    </span>
                    <span className="text-white/30 ml-auto text-[10px]">{formatTime(log.createdAt)}</span>
                  </div>

                  {/* Who updated follow-up status */}
                  {hasUpdater && (
                    <p className="mt-1 text-[10px] text-white/40 flex items-center gap-1">
                      <UserCheck size={10} className="text-white/30" />
                      <span>
                        Followed up by <strong className="text-white/70 font-semibold">{log.dispositionUpdatedBy.firstName}</strong>
                        {log.dispositionUpdatedAt ? ` on ${formatTime(log.dispositionUpdatedAt)}` : ''}
                      </span>
                    </p>
                  )}

                  {/* Follow-up Notes or Reason */}
                  {(hasNotes || log.callReason) && (
                    <div className="mt-2 pt-2 border-t border-white/[0.05] space-y-1">
                      {log.callReason && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-[10px] font-medium text-indigo-300">
                          <MessageSquare size={9} />
                          Query: {log.callReason}
                        </span>
                      )}
                      {hasNotes && (
                        <p className="text-[11px] text-white/70 italic bg-white/[0.02] p-1.5 rounded border border-white/[0.04]">
                          "{log.dispositionNotes || log.notes}"
                        </p>
                      )}
                    </div>
                  )}

                  {/* Audio Recording Player if available */}
                  {log.recordingUrl && (
                    <div className="mt-2 pt-2 border-t border-white/[0.05]">
                      <p className="text-[10px] text-white/40 font-semibold mb-1 flex items-center gap-1">
                        <Play size={10} /> Call Recording:
                      </p>
                      <audio controls src={log.recordingUrl} className="w-full h-8 opacity-80" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Modal: Update Follow-up Status ─── */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden text-slate-800">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-indigo-600" />
                <h3 className="text-sm font-bold text-slate-800">Update Follow-up Status</h3>
              </div>
              <button 
                onClick={() => !savingDisp && setSelectedLog(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveDisposition} className="p-4 space-y-3.5">
              {/* Context */}
              <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/70 text-xs text-slate-600 space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-500">Call Time:</span>
                  <span className="font-semibold text-slate-800">{formatTime(selectedLog.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-slate-500">Call Status:</span>
                  <span className="font-semibold text-slate-700">{STATUS_CONFIG[selectedLog.status]?.label || selectedLog.status}</span>
                </div>
              </div>

              {saveSuccessMsg && (
                <div className="p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-600" />
                  {saveSuccessMsg}
                </div>
              )}

              {/* Status dropdown */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Follow-up Status <span className="text-rose-500">*</span>
                </label>
                <select
                  value={dispForm.disposition}
                  onChange={e => setDispForm({ ...dispForm, disposition: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium bg-white text-slate-800 outline-none focus:border-indigo-500"
                  required
                >
                  <option value="callback_completed">🟢 Callback Completed / Resolved</option>
                  <option value="follow_up_needed">🔵 Follow-up Needed (Call Again Later)</option>
                  <option value="appointment_booked">🟣 Appointment Booked</option>
                  <option value="not_interested">⚪ Patient Not Interested</option>
                  <option value="invalid_number">🔴 Invalid / Wrong Phone Number</option>
                  <option value="pending">🟡 Pending Callback (Unresolved)</option>
                </select>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Call Reason / Medical Inquiry
                </label>
                <input
                  type="text"
                  placeholder="e.g. Knee Surgery inquiry, Consultation fee"
                  value={dispForm.callReason}
                  onChange={e => setDispForm({ ...dispForm, callReason: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-indigo-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Follow-up Notes & Remarks
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Called customer on 9 Sept. Answered surgery inquiry, appointment scheduled."
                  value={dispForm.dispositionNotes}
                  onChange={e => setDispForm({ ...dispForm, dispositionNotes: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-indigo-500 resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="pt-2 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={savingDisp}
                  onClick={() => setSelectedLog(null)}
                  className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingDisp}
                  className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 flex items-center gap-1 shadow-sm"
                >
                  {savingDisp ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
                  <span>Save Status</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallHistoryPanel;
